import React, { useCallback, useMemo, useState } from "react";
import { Box, useColorModeValue, Stack, Tooltip, useBreakpointValue, Center, Button, Image } from "@chakra-ui/react";
import { useButtonProps, useTileProps } from "hooks";
import { MarkdownItemView } from "screens/markdown/MarkdownItemView";
import { StockEquityChart } from "screens/collection/components/StockEquityChart";
import { useCopyValue } from "hooks/useCopies";
import { AddIcon } from "@chakra-ui/icons";
import { TextOverflowTooltip } from "screens/landing/components/TextOverflowTooltip";
import { generateMockStockEquityData } from "types/stock";
import { ShowMoreContainer } from "screens/common/components/ShowMoreContainer";
import { StockIndicatorSmall } from "./components/StockIndicatorSmall";
import { useConversationDialogSubmitFunctions } from "screens/thread/useConversationDialogSubmitFunctions";
import charliLogo from "screens/common/static/logos/charli-logo-full.svg";

interface ProjectLandingSmallTileProps {
  companyLogoUrl?: string;
  companyName?: string;
  ticker: string;
  exchange: string;
  companyDescription?: string;
}

const ProjectLandingSmallTilePlaceholder: React.FC<ProjectLandingSmallTileProps> = ({
  companyLogoUrl,
  companyName,
  ticker,
  exchange,
  companyDescription,
}) => {
  const tileBgColor = useColorModeValue("white", "#161B25");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("primary.darkGray", "gray.300");
  const overlayColor = useColorModeValue("rgba(255, 255, 255, 0.7)", "rgba(26, 32, 44, 0.7)");
  const buttonBgColor = useColorModeValue("primary.default", "gray.900");
  const buttonColor = useColorModeValue("gray.50", "primary.default");
  const buttonColorHover = useColorModeValue("white", "gray.100");
  const commonButtonProps = useButtonProps("md", "cta");
  const [isRequesting, setIsRequesting] = useState(false);
  const commonTileProps = useTileProps();
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { fallback: "md", ssr: false });

  const placeholderStockEquityData = useMemo(() => generateMockStockEquityData(), []);
  const placeholderTileText = useCopyValue("copy_project_placeholder_message");
  const loadingText = useCopyValue("copy_project_starting_message");
  const analysisButtonLabel = useCopyValue("copy_placeholder_analysis_button_label");

  const { onSubmitDueDiligenceProject } = useConversationDialogSubmitFunctions({});

  const onStartDueDiligence = useCallback(() => {
    if (!ticker || !exchange) {
      return;
    }
    setIsRequesting(true);
    onSubmitDueDiligenceProject({ ticker, exchange, companyLogoUrl, companyDescription, companyName });
  }, [ticker, exchange, onSubmitDueDiligenceProject, companyLogoUrl, companyDescription, companyName]);

  return (
    <>
      <Box
        {...commonTileProps}
        bgColor={tileBgColor}
        p="0"
        overflow="visible"
        textAlign="left"
        className={"ch-project-landing-placeholder-tile"}>
        <Stack spacing={"1rem"} direction="column" h="full" p="1rem">
          <Stack direction="row" justifyContent={"space-between"} width="100%" alignItems="center">
            <Stack direction={"row"} spacing={"0.5rem"} alignItems="center">
              <TextOverflowTooltip label={companyName || ""} color={textColor} noOfLines={1} className="ch-project-landing-title" />
            </Stack>
          </Stack>
          <Stack direction={"row"} spacing={"1rem"} alignItems="top" justifyContent={"space-between"}>
            <Stack minWidth={isMobile ? "5rem" : "10rem"} overflow={"hidden"}>
              <Stack spacing={"1.5rem"} alignItems="left" position={"relative"}>
                <Box justifyContent={"left"} width="100%">
                  {companyLogoUrl ? (
                    <Image
                      maxHeight="2rem"
                      fit={"contain"}
                      src={companyLogoUrl}
                      backgroundColor={"transparent"}
                      border={`1px solid ${borderColor}`}
                      flexShrink={0}
                      width={300}
                      height={60}
                      loading="lazy"
                    />
                  ) : (
                    <Image
                      maxHeight="1.5rem"
                      fit={"contain"}
                      src={charliLogo}
                      backgroundColor={"transparent"}
                      border={`1px solid ${borderColor}`}
                      flexShrink={0}
                      loading="lazy"
                    />
                  )}
                </Box>
              </Stack>
            </Stack>
            <Box color={textColor}>
              <ShowMoreContainer minHeight="6.5rem" height="4.5rem">
                <MarkdownItemView markdown={placeholderTileText || loadingText} />
              </ShowMoreContainer>
            </Box>
          </Stack>
          <Stack direction="row" justify="space-between" align="center" spacing="4.4rem">
            <Tooltip zIndex={2} aria-label="stock-equity-tooltip">
              <Stack direction="row" borderColor={borderColor} borderWidth={"1px"} borderRadius={"md"} px="1rem" py=".5rem" width="100%">
                <StockEquityChart height="4rem" hideAxis hideBorder stockEquityData={placeholderStockEquityData} />
                <Center>
                  <StockIndicatorSmall ticker={ticker} exchange={exchange} />
                </Center>
              </Stack>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          borderRadius={"lg"}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          backgroundColor={overlayColor}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <Button
            className="ch-start-analysis-button"
            {...commonButtonProps}
            bgColor={buttonBgColor}
            borderColor={buttonBgColor}
            color={buttonColor}
            _hover={{ color: buttonColorHover }}
            isDisabled={isRequesting}
            onClick={onStartDueDiligence}
            leftIcon={<AddIcon />}>
            {`${analysisButtonLabel} ${ticker}`}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default ProjectLandingSmallTilePlaceholder;
